import { useCallback, useEffect, useMemo } from "react";
import {
  IRShellMeshData,
  LaserHeatModel,
  LaserScanDataModel,
  LidarZoneSelectionModel,
} from "../types/laserScanScene";
import SceneWorker, { SceneWorkerActions } from "../workers/sceneWorker";
import { CurrentProcessedThermalImageDataModel } from "../../irScanAnalysis/hooks/useGetProcessedThermalImages";
import WorkerFactory from "~/workers/workerFactory";
import {
  INFRARED_MAX_VALUE,
  INFRARED_MIN_VALUE,
  LIVE_QUADRANT_ORDER,
  positionSuffixMap,
} from "../constant/laserScene";
import colorMap from "colormap";
import { isImperial } from "~/helpers/userPreference";
import {
  VISUAL_MAP_BOTTOM_COLORS_SCALE,
  VISUAL_MAP_SIDEWALL_COLORS_SCALE,
} from "../../../constants/heatMapValues";
import { calculateDelaunayMeshByUsingDelunator } from "../helpers/laserSceneCalculator";

export const useLaserSceneWorker = () => {
  const workerInstance = useMemo(() => WorkerFactory(SceneWorker), []);

  workerInstance.postMessage({
    type: SceneWorkerActions.INITIALIZE,
    payload: {
      sidewallColorsScale: VISUAL_MAP_SIDEWALL_COLORS_SCALE,
      bottomColorsScale: VISUAL_MAP_BOTTOM_COLORS_SCALE,
      infraredMinValue: INFRARED_MIN_VALUE,
      infraredMaxValue: INFRARED_MAX_VALUE,
      quadrantOrder: LIVE_QUADRANT_ORDER,
      positionSuffixMap: positionSuffixMap,
      colorMap: colorMap({
        colormap: "jet",
      }),
      isImperial: isImperial(),
    },
  });

  useEffect(() => {
    return () => {
      workerInstance.terminate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculatePointCloud = useCallback(
    (
      data: string | ArrayBuffer,
      tempHeat: LaserHeatModel,
      agreedHeatData: LidarZoneSelectionModel[],
    ): Promise<LaserScanDataModel> => {
      return new Promise((resolve) => {
        if (!workerInstance) return;

        const handleMessage = (event: MessageEvent) => {
          if (event.data.type === SceneWorkerActions.POINT_CLOUD_RESULT) {
            workerInstance?.removeEventListener("message", handleMessage);
            resolve(event.data.payload);
            workerInstance.terminate();
          }
        };

        workerInstance.addEventListener("message", handleMessage);
        workerInstance.postMessage({
          type: SceneWorkerActions.CALCULATE_POINT_CLOUD,
          payload: { data, tempHeat, agreedHeatData },
        });
      });
    },
    [workerInstance],
  );

  const calculateIRShell = useCallback(
    (
      processedThermalImages: CurrentProcessedThermalImageDataModel,
    ): Promise<IRShellMeshData[]> => {
      return new Promise((resolve) => {
        if (!workerInstance) return;

        const handleMessage = (event: MessageEvent) => {
          if (event.data.type === SceneWorkerActions.IR_SHELL_RESULT) {
            workerInstance?.removeEventListener("message", handleMessage);
            resolve(event.data.payload);
            workerInstance.terminate();
          }
        };

        workerInstance.addEventListener("message", handleMessage);
        workerInstance.postMessage({
          type: SceneWorkerActions.CALCULATE_IR_SHELL,
          payload: { processedThermalImages },
        });
      });
    },
    [workerInstance],
  );

  const calculateDelaunayMesh = useCallback(
    (heatData: LaserScanDataModel) => {
      return new Promise((resolve) => {
        if (!workerInstance) return;

        const handleMessage = (event: MessageEvent) => {
          if (event.data.type === SceneWorkerActions.DELAUNAY_RESULT) {
            workerInstance?.removeEventListener("message", handleMessage);
            const { points3dFull2, geometryData } = event.data.payload;
            const result = calculateDelaunayMeshByUsingDelunator(
              points3dFull2,
              geometryData,
            );
            resolve(result);
            workerInstance.terminate();
          }
        };

        workerInstance.addEventListener("message", handleMessage);
        workerInstance.postMessage({
          type: SceneWorkerActions.CALCULATE_DELAUNAY_MESH,
          payload: { heatData },
        });
      });
    },
    [workerInstance],
  );

  return {
    calculatePointCloud,
    calculateIRShell,
    calculateDelaunayMesh,
  };
};
